import React, { useContext, useState } from 'react';
import { updateEventDetails, Event, getEventName, deleteEvent } from '../../../../../../model/event';
import { Button } from '../../../../../../components/Button/Button';
import { Modal } from '../../../../../../components/Modal/Modal';
import { KeyValueTable } from '../../../../../../components/KeyValueTable/KeyValueTable';
import { formatDateTime } from '../../../../../../util/util';
import { handleError } from '../../../../../../model/notification';
import { t } from '../../../../../../locales/translate';
import { useConfirmation } from '../../../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { eventsPath } from '../../../../../../routes';
import { history } from '../../../../../../model/history';
import { Stack } from '../../../../../../components/Stack/Stack';
import { ActivitiesContext } from '../../../../../../components/Activities/Activities';

interface Props {
  event: Event;
}

export function StatusDraft({ event }: Props): JSX.Element {
  const [displayModal, setDisplayModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { reloadActivities } = useContext(ActivitiesContext);

  async function handleOnClick(): Promise<void> {
    setLoading(true);
    try {
      await updateEventDetails({
        eventId: event.id,
        changes: { status: 'in_review' },
      });
      reloadActivities();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  const { confirm } = useConfirmation({
    title: t.model.event.deleteEventTitle,
    message: t.model.event.deleteEventConfirmation,
    action: async () => {
      await deleteEvent(event.id);
      history.push(eventsPath());
    },
  });

  return (
    <div className="event-status__draft">
      <Stack className="event-status__details">
        <div className="event-status__title">{t.model.status.draft}</div>
        <div>{t.model.event.draftMsg}</div>
        {event.$permissions.includes('request_review') && (
          <div className="event-status__actions">
            <Button
              title={t.model.sale.requestSale}
              type="primary"
              onClick={() => {
                setDisplayModal(true);
              }}
              loading={loading}
            />
            <Button
              className="event-status__actions delete-button"
              title={t.model.event.deleteEventTitle}
              onClick={confirm}
            />
          </div>
        )}
      </Stack>
      <Modal
        size="small"
        title={t.model.sale.requestSale}
        open={displayModal}
        className="status-draft__modal"
        footer={
          <>
            <Button
              title={t.actions.cancel('')}
              onClick={() => {
                setDisplayModal(false);
              }}
            />
            <Button onClick={handleOnClick} title={t.model.event.sendRequest} type="primary" loading={loading} />
          </>
        }
      >
        <Stack>
          <p>{t.model.event.confirmRequestMsg}</p>
          <p>
            <strong>Summary</strong>
          </p>
          <KeyValueTable
            items={[
              { key: t.model.event.eventTitle, value: getEventName(event) },
              {
                key: t.model.datetime._name,
                value: formatDateTime(event.happeningFrom),
              },
              {
                key: t.model.venue._name,
                value: event.venue && event.venue.name,
              },
              { key: `${t.model.ticket._name} ${t.model.price._name}` },
              {
                key: t.model.event.publishAt,
                value: formatDateTime(event.publishedFrom),
              },
              {
                key: t.model.event.owner,
                value: event.owningCompany && event.owningCompany.name,
              },
            ]}
          />
        </Stack>
      </Modal>
    </div>
  );
}
